// Libs
import classNames from 'classnames/bind';
import { Grid2, IconButton } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
// Components, Layouts, Pages
import { BaseInput, BaseSelect } from '~/components/common';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { formatInputDecimal } from '~/utils/helper/common';
import { ISelectService } from '~/utils/interface/service';
import { IFormEstimateTemplate } from '~/utils/interface/estimateTemplate';
// Styles, images, icons
import styles from './EstimateTemplateService.module.scss';
import { icons } from '~/assets';

type Props = {
  index: number;
  options?: ISelectService[];
  isLoading?: boolean;
  onRemoveItem: (index: number) => void;
};

const cx = classNames.bind(styles);

const EstimateTemplateService = (props: Props) => {
  //#region Destructuring Props
  const { index, options, isLoading, onRemoveItem } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimateTemplate']);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IFormEstimateTemplate>();
  const price = useWatch({ control, name: `estimateServices.${index}.price` });
  const quantity = useWatch({ control, name: `estimateServices.${index}.quantity` });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue(`estimateServices.${index}.total`, String(Number(price) * Number(quantity)), {
      shouldDirty: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, quantity]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOnchangeService = (value: ISelectService) => {
    setValue(`estimateServices.${index}.serviceId`, value?.value || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateServices.${index}.price`, value?.price || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateServices.${index}.cost`, value?.cost || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateServices.${index}.quantity`, value?.quantity || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`estimateServices.${index}.description`, value?.description || EMPTY_STRING, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  //#endregion Handle Function

  return (
    <Grid2
      component='div'
      id='estimateTemplateServicesComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2
        size={'grow'}
        container
        rowSpacing={'12px'}
        columnSpacing={'12px'}
        className={cx(index !== DEFAULT_NUMBER_ZERO ? 'item' : EMPTY_STRING)}
      >
        <Grid2 size={'grow'}>
          <Controller
            name={`estimateServices.${index}.serviceId`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseSelect
                label={t('field_service_name_label')}
                value={value || EMPTY_STRING}
                onChange={(optionValue) => handleOnchangeService(optionValue)}
                options={options || []}
                isLoading={isLoading}
                errorMessage={errors?.estimateServices?.[index]?.serviceId?.message}
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateServices.${index}.description`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_service_description_label')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.estimateServices?.[index]?.description?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateServices.${index}.price`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_service_price_label')}
                value={value}
                required
                onChange={(event) => {
                  const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                  const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                  onChange(number);
                }}
                errorMessage={errors?.estimateServices?.[index]?.price?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateServices.${index}.cost`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_service_unit_label')}
                value={value}
                required
                onChange={(event) => {
                  const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                  const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                  onChange(number);
                }}
                errorMessage={errors?.estimateServices?.[index]?.cost?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={'grow'}>
          <Controller
            name={`estimateServices.${index}.quantity`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                label={t('field_service_quantity_label')}
                value={value}
                required
                onChange={(event) => {
                  onChange(formatInputDecimal(event.target.value));
                }}
                errorMessage={errors?.estimateServices?.[index]?.quantity?.message}
              />
            )}
          />
        </Grid2>
      </Grid2>

      <Grid2 size='auto' className='flex justify-center items-center'>
        <IconButton className={cx('closeField')} onClick={() => onRemoveItem(index)}>
          <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>
      </Grid2>
    </Grid2>
  );
};

export default EstimateTemplateService;
